import {
  Box,
  Button,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Drawer } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { layoutStyle } from "../layout-ui/style";
import { useCompany, useDealer } from "../../common/hooks";
import { localStorageItemNames } from "../../common/constants";
import { TruckLoader } from "./truckLoader";

export const AddDealer = ({
  open,
  setOpen,
  editValue,
  handleClose,
  setRefreshData,
}) => {
  const classes = layoutStyle();
  const { createDealerApi, editDealerApi } = useDealer();
  const { company, getCompanyApi } = useCompany();
  const [dealerName, setDealerName] = useState("");
  const [address, setAddress] = useState("");
  const [status, setStatus] = useState("0"); // 0 = Active, 1 = Inactive
  const [selectedCompany, setSelectedCompany] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValues = {
    dealerName: "",
    address: "",
    status: "0",
    allowCompanies: [],
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: defaultValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    getCompanyApi({ limit: 10000, offset: 0 });
  }, [getCompanyApi]);

  useEffect(() => {
    if (editValue) {
      const nextDealerName = editValue?.dealerName || "";
      const nextAddress = editValue?.address || "";
      const nextCompanies = (editValue?.companies || [])
        .map((item) => item?.id)
        .filter((id) => id !== undefined && id !== null);
      let nextStatus = "1";

      setDealerName(nextDealerName);
      setAddress(nextAddress);
      setSelectedCompany(nextCompanies);
      // Handle status - support both numeric (0/1) and string values
      if (
        editValue?.status === 0 ||
        editValue?.status === "0" ||
        editValue?.status === true ||
        editValue?.status === "true" ||
        editValue?.status === "True" ||
        editValue?.status === "Active" ||
        editValue?.status === "active"
      ) {
        nextStatus = "0";
      } else if (
        editValue?.status === 1 ||
        editValue?.status === "1" ||
        editValue?.status === false ||
        editValue?.status === "false" ||
        editValue?.status === "False" ||
        editValue?.status === "Inactive" ||
        editValue?.status === "inactive"
      ) {
        nextStatus = "1";
      }
      setStatus(nextStatus);
      reset({
        dealerName: nextDealerName,
        address: nextAddress,
        status: nextStatus,
        allowCompanies: nextCompanies,
      });
    } else {
      setDealerName("");
      setAddress("");
      setStatus("0");
      setSelectedCompany([]);
      reset(defaultValues);
    }
  }, [editValue, open, reset]);

  const onSubmit = async (values) => {
    try {
      setIsSubmitting(true);
      const userTypeId = localStorage.getItem(localStorageItemNames.userID);
      const payload = {
        dealerName: dealerName,
        address: address,
        status: status === "0", // DealerDto.Status is nullable bool: true = Active, false = Inactive
        allowCompanies: selectedCompany,
        createdBy: Number(userTypeId),
        updatedBy: Number(userTypeId),
      };

      if (editValue) {
        const editPayload = {
          ...payload,
          id: editValue?.id,
        };
        await editDealerApi(editPayload);
      } else {
        await createDealerApi(payload);
      }

      setRefreshData?.(true);
      handleClose?.();
      reset();
      setDealerName("");
      setAddress("");
      setStatus("0");
      setSelectedCompany([]);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOnClose = () => {
    setDealerName("");
    setAddress("");
    setStatus("0");
    setSelectedCompany([]);
    reset();
    handleClose?.();
  };

  return (
    <React.Fragment>
      <Drawer
        onClose={handleOnClose}
        open={open}
        maxWidth="sm"
        anchor="bottom"
        fullHeight
        style={{ top: "100px", bottom: "56px !important" }}
        className="DrawerName"
      >
        <DialogTitle
          className={classes.dailogTitle}
          id="customized-dialog-title"
        >
          {editValue ? "Edit Dealer" : "Add Dealer"}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleOnClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          dividers
          style={{ position: "relative", minHeight: "400px" }}
        >
          {isSubmitting && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                zIndex: 1000,
              }}
            >
              <TruckLoader />
            </Box>
          )}
          <Grid container className={classes.disFlexCenter}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid
                sx={{ width: "30vw", marginY: 1, paddingX: 2 }}
                className={classes.companyForm}
              >
                <Grid className={classes.compnayFormField}>
                  <InputLabel style={{ fontSize: "12px" }}>
                    Dealer Name
                  </InputLabel>
                  <TextField
                    type="text"
                    fullWidth
                    color="success"
                    InputProps={{ style: { fontSize: "12px", height: "35px" } }}
                    value={dealerName}
                    {...register("dealerName", {
                      required: "Dealer name is required",
                    })}
                    onChange={(e) => {
                      setDealerName(e.target.value);
                      setValue("dealerName", e.target.value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    error={!!errors?.dealerName}
                    helperText={errors?.dealerName?.message}
                  />
                </Grid>

                <Grid className={classes.compnayFormField}>
                  <InputLabel style={{ fontSize: "12px" }}>Address</InputLabel>
                  <TextField
                    type="text"
                    fullWidth
                    color="success"
                    InputProps={{ style: { fontSize: "12px", height: "35px" } }}
                    value={address}
                    {...register("address")}
                    onChange={(e) => {
                      setAddress(e.target.value);
                      setValue("address", e.target.value, {
                        shouldDirty: true,
                      });
                    }}
                  />
                </Grid>

                <Grid className={classes.compnayFormField}>
                  <InputLabel style={{ fontSize: "12px" }}>
                    Company Name
                  </InputLabel>
                  <Select
                    fullWidth
                    multiple
                    style={{ fontSize: "12px" }}
                    value={selectedCompany}
                    {...register("allowCompanies")}
                    onChange={(event) => {
                      const nextCompanies = event.target.value;
                      setSelectedCompany(nextCompanies);
                      setValue("allowCompanies", nextCompanies, {
                        shouldDirty: true,
                      });
                    }}
                    renderValue={(selected) =>
                      company
                        ?.filter((item) => selected.includes(item.id))
                        .map((item) => item.companyName)
                        .join(", ")
                    }
                  >
                    {company?.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.companyName}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>

                <Grid className={classes.compnayFormField}>
                  <InputLabel style={{ fontSize: "12px" }}>Status</InputLabel>
                  <Select
                    style={{ fontSize: "12px" }}
                    value={status}
                    {...register("status")}
                    onChange={(event) => {
                      const nextStatus = event.target.value;
                      setStatus(nextStatus);
                      setValue("status", nextStatus, {
                        shouldDirty: true,
                      });
                    }}
                  >
                    <MenuItem value={"0"}>Active</MenuItem>
                    <MenuItem value={"1"}>Inactive</MenuItem>
                  </Select>
                </Grid>

                <Button
                  variant="outlined"
                  type="submit"
                  className={classes.fleetListNewButton}
                  autoFocus
                  fullWidth
                  sx={{ mt: 3 }}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Processing..."
                    : editValue
                      ? "Edit Dealer"
                      : "Add Dealer"}
                </Button>
              </Grid>
            </form>
          </Grid>
        </DialogContent>
      </Drawer>
    </React.Fragment>
  );
};
